import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormGroup, FormControl, Validators } from '@angular/forms';
// Rxjs dependencies.
import { BehaviorSubject, merge, Subject } from 'rxjs';
import { map, tap, takeUntil } from 'rxjs/operators';
// Relative dependencies.
import { AuthService, ApiService } from '../core/upgraded-providers';
import { ConfirmService } from '../ui.module';

function sum(obj): number {
  return Object.values(obj)
    .reduce((acc: number, val: string | number) => acc + (typeof val === 'string' ? parseInt(val, 10) : val), 0) as number;
}

@Component({
  selector: 'scw-shards',
  templateUrl: './shards.component.pug',
  styleUrls: ['./shards.component.less'],
})
export class SCWShardsComponent implements OnInit, OnDestroy {
  private _destroy$ = new Subject<void>();

  private _cache;
  protected form: FormGroup = new FormGroup({});

  protected balance = 0;
  protected shards$ = new BehaviorSubject<ISCWShardsItem[]>(this._route.snapshot.data['shards']);
  protected shardsN$ = this.shards$.pipe(map((shards) => shards.filter(({ name }) => name !== 'shardX')));
  protected shardX$ = this.shards$.pipe(map((shards) => shards.find(({ name }) => name === 'shardX')));
  protected cpuShard = this._authService.Me.cpuShard || {};
  protected sumCpu = sum(this.cpuShard) || 0;
  protected assignCpuMode = false;

  constructor(
    private _route: ActivatedRoute,
    private _authService: AuthService,
    private _apiService: ApiService,
    private _confirm: ConfirmService
  ) {
    this._authService.required();
  }

  public ngOnInit(): void {
    this.shards$.getValue().forEach(({ name }) => {
      const shardCpu = parseInt(this.cpuShard[name], 10) || 0;

      this.form.addControl(name, new FormControl(shardCpu, [
        Validators.min(0),
        Validators.max(this.sumCpu),
        Validators.required
      ]));
    });

    const controls = Object.values(this.form.controls).map((control) => {
      return control.valueChanges.pipe(
        map(() => control),
      );
    });

    this.form.disable();

    merge(...controls).pipe(
      takeUntil(this._destroy$),
      tap((control) => {
        const controls = Object.values(this.form.controls).filter((c) => c !== control);
        const summary = controls.reduce((acc, item) => acc + item.value, 0);
        const ratio = controls.map((item) => summary ? (item.value / summary) : 0);
        const remainingCpu = this.sumCpu - control.value;

        controls.forEach((item, index) => {
          const value = Math.min(
            item.value,
            Math.round(remainingCpu * ratio[index])
          );

          item.patchValue(value, { emitEvent: false });
        });

        this.balance = this.sumCpu - sum(this.form.value);
      }),
    ).subscribe();
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  protected onCancelReassignCpu() {
    this.form.disable({ emitEvent: false });
    this.form.patchValue(this._cache, { emitEvent: false });
  }

  protected onEnableReassignCpu() {
    this._cache = this.form.getRawValue();
    this.form.enable({ emitEvent: false });
  }

  protected onChangeCpu(name, value) {
    console.log('onChangeCpu', name, value);

    this.form.controls[name].patchValue(value);

    const otherShards = Object.entries(this.form.value).reduce((result, [shardName, val]) => {
      if (shardName !== name) {
        result[shardName] = val;
      }

      return result;
    }, {});

    const otherShardsSum: number = sum(otherShards);
    const otherShardsLength = Object.keys(otherShards).length;

    const otherShardsRatio = Object.entries(otherShards)
      .reduce((acc, [shardName, val]: [string, number]) => {
        const value = otherShardsSum > 0 ? (val / otherShardsSum) : (1 / otherShardsLength);
        return Object.assign({}, acc, { [shardName]: value });
      }, {});

    Object.keys(otherShards)
      .every((shardName) => {
        const value = Math.min(
          this.form.value[shardName],
          Math.round((this.sumCpu - this.form.value[name]) * otherShardsRatio[shardName])
        );

        this.form.controls[shardName].patchValue(value);

        return this.sumCpu !== sum(this.form.value);
      });

    this.balance = this.sumCpu - sum(this.form.value);
  }

  protected disabledReassignCpu() {
    return null;
    return (this._authService.Me.cpuShardUpdatedTime >= Date.now() - 12 * 3600 * 1000) || null;
  }

  async onSaveCpu() {
    try {
      await this._confirm.show({ data: {
        innerHTML: `
          <div class='--text-left'>
          <p>You\'re going to re-assign your CPU to the following shards:</p>
          <ul class='text-left'>
            ${ Object.entries(this.form.value).map(([s, v]) => `<li>${ s }: <b>${ v } CPU</b></li>`).join('') }
          </ul>
          <p>You will not be able to change these settings in the next <b>12 hours</b>!<br />Do you want to proceed?</p>
          </div>
        `
      }});
    } catch (err) {
      return;
    }

    this.form.disable({ emitEvent: false });

    const cpuShard = this.cpuShard;
    const cpu = Object.entries(this.form.value).reduce((cpu, [shard, value]: [string, string | number]) => Object.assign({}, cpu, {
      [shard]: typeof value === 'string' ? parseInt(value, 10) : value
    }), {});
    this.cpuShard = cpu;

    try {
      await this._apiService.post('user/cpu-shards', { cpu });
    } catch (err) {
      this.cpuShard = cpuShard;
    }

    this._authService.check();
  }
}
